<script setup>
  import { ref, useTemplateRef, watchEffect, onUnmounted } from 'vue';
  import useUserHints from '@/composables/useUserHints';

  const props = defineProps({
    position: {
      type: String,
      default: 'right',
    },
    hintDomain: {
      type: String,
      required: true,
    },
  });

  const { domain } = useUserHints();

  const hintElement = useTemplateRef('hint-element');

  const pixelsPosition = ref({ left: '0px', top: '0px' });
  const showHint = ref(false);
  const teleportHint = ref(false);
  const animationendListener = ref(false);
  const parentButton = ref(null);
  const tooltipTimeout = ref(null);
  const mode = ref('hidden');

  const getpixelsPosition = () => {
    if (!hintElement.value) {
      return { left: '0px', top: '0px' }
    }

    const target = parentButton.value || hintElement.value?.parentElement;

    if (!target) {
      return {
        left: '0px',
        top: '0px',
      };
    }

    const targetRect = target.getBoundingClientRect();

    const x = props.position === 'right' ? targetRect.left + targetRect.width + 12 : targetRect.left - 12;
    const y = targetRect.top + targetRect.height / 2;

    return {
      left: `${x}px`,
      top: `${y}px`,
    };
  };

  function handleTransitionEnd() {
    teleportHint.value = mode.value !== 'hidden';
  }

  watchEffect(() => {
    if (!hintElement.value || domain.value !== props.hintDomain) {
      showHint.value = false;
      mode.value = 'hidden';

      return;
    };

    parentButton.value = parentButton.value || hintElement.value.parentElement;

    // We need a slight delay to allow for the DOM to update before calculating position
    setTimeout(() => {
      mode.value = 'domain';
      pixelsPosition.value = getpixelsPosition();
      showHint.value = true;
      teleportHint.value = true;
    }, 200);
  });

  watchEffect(() => {
    if (!hintElement.value || animationendListener.value || parentButton.value) return;

    animationendListener.value = hintElement.value.addEventListener('transitionend', handleTransitionEnd);

    parentButton.value = parentButton.value || hintElement.value.parentElement;

    parentButton.value.addEventListener('mouseenter', (e) => {
      if (mode.value === 'domain') return;
          // We need a slight delay to allow for the DOM to update before calculating position
        tooltipTimeout.value = setTimeout(() => {
          mode.value = 'hover';
          pixelsPosition.value = getpixelsPosition();
          showHint.value = true;
          teleportHint.value = true;
        }, 400);
      
    });
    
    parentButton.value.addEventListener('mouseleave', (e) => {
      if (mode.value === 'domain') return;

      clearTimeout(tooltipTimeout.value);

      mode.value = 'hidden';
      showHint.value = false;
    });
  });

  onUnmounted(() => {
    if (animationendListener.value && hintElement.value) {
      hintElement.value.removeEventListener('transitionend', handleTransitionEnd);
    }
  });

</script>

<template>
  <Teleport
    to="body"
    :disabled="!teleportHint"
  >
    <Transition name="fade">
      <div
        v-show="showHint"
        ref="hint-element"
        class="jwx-user-hint"
        :data-position="props.position"
        :style="`--user-hint-posX: ${pixelsPosition.left}; --user-hint-posY: ${pixelsPosition.top}`"
      >
        <p class="jwx-user-hint__text">
          <slot></slot>
        </p>
      </div>
    </Transition>
  </Teleport>
</template>